"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import type { ComponentProps } from "react";
import { DateRangePicker } from "@xefi/x-react/datepicker";
import { IconFilterOff } from "@xefi/x-react/icons";
import { useTranslation } from "react-i18next";
import type { Search } from "../../../../domain/models/http/SearchRequest";
import { CustomTooltip } from "@/components/shared/Dashboard/CustomTooltip";
import { MultiSelectAutoComplete } from "@/components/shared/Dashboard/MultiSelectAutoComplete";
import { useDomas } from "@/hooks/useDomas";
import { usePaymentBankAccounts } from "@/hooks/usePaymentBankAccounts";
import { useSubsidiaries } from "@/hooks/useQueries";
import { usePaymentsFiltersStore } from "@/store/usePaymentsFiltersStore";
import type { Option } from "@/types/common";
import { formatAccountLabel } from "./paymentsFormat";
type DateRangeValue = ComponentProps<typeof DateRangePicker>["value"];
const PAYMENT_SUBSIDIARIES_SEARCH: Search = {
  scopes: [{
    name: "withPaymentPlaque"
  }],
  limit: 100
};
type Props = {
  isAccountFilter?: boolean;
};
export default function PaymentsSharedFilters(t0) {
  const $ = _c(77);
  const {
    isAccountFilter: t1
  } = t0;
  const isAccountFilter = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const subsidiaryId = usePaymentsFiltersStore(_temp);
  const domaIds = usePaymentsFiltersStore(_temp2);
  const dateRange = usePaymentsFiltersStore(_temp3);
  const setSubsidiaryId = usePaymentsFiltersStore(_temp4);
  const setDomaIds = usePaymentsFiltersStore(_temp5);
  const dealerships = usePaymentsFiltersStore(_temp6);
  const setDealerships = usePaymentsFiltersStore(_temp7);
  const setDateRange = usePaymentsFiltersStore(_temp8);
  const reset = usePaymentsFiltersStore(_temp9);
  const {
    data: subsidiariesData
  } = useSubsidiaries(PAYMENT_SUBSIDIARIES_SEARCH);
  const {
    data: domasData
  } = useDomas(subsidiaryId);
  const {
    data: bankAccountsData
  } = usePaymentBankAccounts(subsidiaryId);
  subsidiariesData?.data;
  let t2;
  if ($[0] !== subsidiariesData?.data) {
    t2 = subsidiariesData?.data ?? [];
    $[0] = subsidiariesData?.data;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = t2.map(_temp10);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const subsidiaryOptions = t3;
  let t4;
  if ($[4] !== domasData?.data) {
    t4 = () => (domasData?.data ?? []).map(_temp11);
    $[4] = domasData?.data;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  domasData?.data;
  let t5;
  if ($[6] !== t4) {
    t5 = t4();
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const domaOptions = t5;
  let t6;
  if ($[8] !== subsidiaryId || $[9] !== subsidiaryOptions) {
    let t7;
    if ($[11] !== subsidiaryId) {
      t7 = option => String(option.id) === subsidiaryId;
      $[11] = subsidiaryId;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = subsidiaryOptions.find(t7);
    $[8] = subsidiaryId;
    $[9] = subsidiaryOptions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const match = t6;
  let t7;
  if ($[13] !== match) {
    t7 = match ? [match] : [];
    $[13] = match;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const selectedSubsidiary = t7;
  let t8;
  if ($[15] !== domaIds || $[16] !== domaOptions) {
    let t9;
    if ($[18] !== domaIds) {
      t9 = option_0 => domaIds.includes(String(option_0.id));
      $[18] = domaIds;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = domaOptions.filter(t9);
    $[15] = domaIds;
    $[16] = domaOptions;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const selectedDomas = t8;
  let t9;
  if ($[20] !== bankAccountsData?.data) {
    t9 = () => {
      const idByName = new Map();
      for (const bankAccount of bankAccountsData?.data ?? []) {
        if (bankAccount.dealership && !idByName.has(bankAccount.dealership)) {
          idByName.set(bankAccount.dealership, bankAccount.id);
        }
      }
      return new Map([...idByName].map(_temp12));
    };
    $[20] = bankAccountsData?.data;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  bankAccountsData?.data;
  let t10;
  if ($[22] !== t9) {
    t10 = t9();
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const accountNameById = t10;
  let t11;
  if ($[24] !== accountNameById) {
    t11 = [...accountNameById].map(_temp13);
    $[24] = accountNameById;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const accountOptions = t11;
  let t12;
  if ($[26] !== accountNameById || $[27] !== accountOptions || $[28] !== dealerships) {
    t12 = accountOptions.filter(option_1 => dealerships.includes(accountNameById.get(String(option_1.id)) ?? ""));
    $[26] = accountNameById;
    $[27] = accountOptions;
    $[28] = dealerships;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const selectedAccounts = t12;
  const isAnyFilterActive = Boolean(subsidiaryId) || domaIds.length > 0 || dealerships.length > 0 || dateRange != null;
  let t13;
  if ($[30] !== t) {
    t13 = t("paymentsReceived.filters.subsidiary");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== setSubsidiaryId) {
    t14 = value => setSubsidiaryId(value[0] ? String(value[0].id) : null);
    $[32] = setSubsidiaryId;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== selectedSubsidiary || $[35] !== subsidiaryOptions || $[36] !== t13 || $[37] !== t14) {
    t15 = <MultiSelectAutoComplete name="subsidiary" placeholder={t13} options={subsidiaryOptions} selected={selectedSubsidiary} onSelect={t14} selectionMode="single" className="w-56" isFetching={false} />;
    $[34] = selectedSubsidiary;
    $[35] = subsidiaryOptions;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== accountNameById || $[40] !== accountOptions || $[41] !== domaOptions || $[42] !== isAccountFilter || $[43] !== selectedAccounts || $[44] !== selectedDomas || $[45] !== setDealerships || $[46] !== setDomaIds || $[47] !== subsidiaryId || $[48] !== t) {
    t16 = isAccountFilter ? <MultiSelectAutoComplete name="accounts" placeholder={t("paymentsReceived.filters.accounts")} options={accountOptions} selected={selectedAccounts} onSelect={value_0 => setDealerships(value_0.flatMap(option_2 => {
      const dealership_0 = accountNameById.get(String(option_2.id));
      return dealership_0 ? [dealership_0] : [];
    }))} className="w-56" isFetching={false} /> : <MultiSelectAutoComplete name="sites" placeholder={t("paymentsReceived.filters.sites")} options={domaOptions} selected={selectedDomas} onSelect={value_1 => setDomaIds(value_1.map(_temp14))} className="w-56" isFetching={false} isDisabled={!subsidiaryId} />;
    $[39] = accountNameById;
    $[40] = accountOptions;
    $[41] = domaOptions;
    $[42] = isAccountFilter;
    $[43] = selectedAccounts;
    $[44] = selectedDomas;
    $[45] = setDealerships;
    $[46] = setDomaIds;
    $[47] = subsidiaryId;
    $[48] = t;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t) {
    t17 = t("paymentsReceived.filters.period");
    $[50] = t;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const t18 = dateRange as DateRangeValue;
  let t19;
  if ($[52] !== setDateRange) {
    t19 = value_2 => setDateRange(value_2);
    $[52] = setDateRange;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      inputWrapper: "\n                        border !border-border dark:!border-[#3a3a3a] rounded-md shadow-none bg-transparent h-10\n                        group-data-[focus=true]:!border-[#66b8ed] dark:group-data-[focus=true]:!border-primary-400\n                        hover:!border-gray-300 dark:hover:!border-[#4a4a4a]\n                    ",
      base: "dark:text-white",
      input: "text-foreground dark:!text-white dark:placeholder:!text-white",
      innerWrapper: "dark:text-white",
      segment: "dark:!text-white",
      selectorIcon: "dark:!text-white"
    };
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t17 || $[56] !== t18 || $[57] !== t19) {
    t21 = <DateRangePicker aria-label={t17} value={t18} onChange={t19} variant="bordered" radius="sm" className="w-56 max-[1500px]:w-52" classNames={t20} showClearButton={false} />;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t) {
    t22 = t("paymentsReceived.filters.reset");
    $[59] = t;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t) {
    t23 = t("paymentsReceived.filters.reset");
    $[61] = t;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = isAnyFilterActive ? "text-danger" : "text-gray-400 dark:text-white";
  let t25;
  if ($[63] !== t24) {
    t25 = <IconFilterOff size={18} className={t24} />;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== reset || $[66] !== t23 || $[67] !== t25) {
    t26 = <button type="button" aria-label={t23} className="flex h-10 w-10 items-center justify-center rounded-md border border-border transition-colors hover:bg-gray-50 dark:border-zinc-800 dark:hover:bg-zinc-900" onClick={reset}>{t25}</button>;
    $[65] = reset;
    $[66] = t23;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t22 || $[70] !== t26) {
    t27 = <CustomTooltip title={t22}>{t26}</CustomTooltip>;
    $[69] = t22;
    $[70] = t26;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t15 || $[73] !== t16 || $[74] !== t21 || $[75] !== t27) {
    t28 = <div className="mb-4 flex flex-wrap items-start gap-3">{t15}{t16}{t21}{t27}</div>;
    $[72] = t15;
    $[73] = t16;
    $[74] = t21;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  return t28;
}
function _temp14(option_3) {
  return String(option_3.id);
}
function _temp13(t0) {
  const [id_0, dealership] = t0;
  return {
    id: id_0,
    name: formatAccountLabel(dealership, null)
  };
}
function _temp12(t0) {
  const [name, id] = t0;
  return [id, name];
}
function _temp11(doma) {
  return {
    id: doma.id,
    name: `${doma.code} - ${doma.label}`
  };
}
function _temp10(subsidiary) {
  return {
    id: subsidiary.id,
    name: subsidiary.name
  };
}
function _temp9(state_7) {
  return state_7.reset;
}
function _temp8(state_6) {
  return state_6.setDateRange;
}
function _temp7(state_5) {
  return state_5.setDealerships;
}
function _temp6(state_4) {
  return state_4.dealerships;
}
function _temp5(state_3) {
  return state_3.setDomaIds;
}
function _temp4(state_2) {
  return state_2.setSubsidiaryId;
}
function _temp3(state_1) {
  return state_1.dateRange;
}
function _temp2(state_0) {
  return state_0.domaIds;
}
function _temp(state) {
  return state.subsidiaryId;
}